import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Settings2, RotateCcw, Edit2, CheckCircle, AlertTriangle, Info, Code2, BookOpen, Lock, Layers, Users, UserCog, GraduationCap, Shield, ListTodo } from "lucide-react";
import RoadmapPanel from "./RoadmapPanel";

// ─────────────────────────────────────────────────────────────
// renderInlineMarkup — SAFE renderer for `**bold**` and `` `code` `` spans.
// Never injects HTML; always returns a React fragment of Text/JSX nodes.
// Replaces earlier `dangerouslySetInnerHTML` usage.
// ─────────────────────────────────────────────────────────────
function renderInlineMarkup(text, tone = "cyan") {
  if (!text) return null;
  const boldCls = tone === "emerald" ? "text-emerald-200" : "text-cyan-200";
  const codeCls = tone === "emerald"
    ? "text-cyan-300 bg-slate-950/50 px-1 rounded text-[11px]"
    : "text-emerald-300 bg-slate-950/50 px-1 rounded text-[11px]";

  const tokenRe = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  const parts = String(text).split(tokenRe).filter(Boolean);

  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className={boldCls}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return <code key={i} className={codeCls}>{part.slice(1, -1)}</code>;
    }
    return <span key={i}>{part}</span>;
  });
}

// ─────────────────────────────────────────────────────────────
// DateBadge — shows implementation date + "NOVO" pill for recent items
// ─────────────────────────────────────────────────────────────
function DateBadge({ date, recentDays = 30 }) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  const isRecent = diffDays >= 0 && diffDays <= recentDays;
  const formatted = d.toLocaleDateString("pt-PT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return (
    <span className="inline-flex items-center gap-1.5">
      {isRecent && (
        <span
          className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-pulse"
          title={`Adicionado há ${diffDays} dia${diffDays === 1 ? "" : "s"}`}
        >
          NOVO
        </span>
      )}
      <span className="text-[10px] text-slate-500 font-mono" title="Data de implementação">
        {formatted}
      </span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// FeaturesView — read-only catalogue of ALL implemented features
// ─────────────────────────────────────────────────────────────
const AUDIENCE_META = {
  admin: { label: "Admin", icon: Shield, color: "text-red-300 bg-red-500/10 border-red-500/30" },
  instructor: { label: "Instrutor", icon: UserCog, color: "text-blue-300 bg-blue-500/10 border-blue-500/30" },
  student: { label: "Aluno", icon: GraduationCap, color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
};

function FeaturesView({ features, categoryFilter, setCategoryFilter, audienceFilter, setAudienceFilter }) {
  const categories = Object.keys(features.by_category);

  let filtered = features.docs;
  if (categoryFilter !== "all") filtered = filtered.filter((f) => f.category === categoryFilter);
  if (audienceFilter !== "all") filtered = filtered.filter((f) => (f.audience || []).includes(audienceFilter));

  return (
    <div className="space-y-4" data-testid="features-view">
      <Card className="instrument-panel">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Layers className="w-6 h-6 text-emerald-400" />
              <div>
                <CardTitle className="font-chivo text-cockpit-text-bright">Funcionalidades Implementadas</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Catálogo vivo de tudo o que o sistema faz hoje. Atualizado automaticamente ao alterar código.
                </p>
              </div>
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              {features.active_count} de {features.total} ativas
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 self-center mr-1">Categoria:</span>
            <button
              onClick={() => setCategoryFilter("all")}
              className={`px-3 py-1 text-xs rounded-full transition ${
                categoryFilter === "all"
                  ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
                  : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
              }`}
            >
              Todas ({features.total})
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1 text-xs rounded-full transition ${
                  categoryFilter === cat
                    ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
                    : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
                }`}
              >
                {cat} ({features.by_category[cat].length})
              </button>
            ))}
          </div>
          {/* Audience filter */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 self-center mr-1">Público:</span>
            <button
              onClick={() => setAudienceFilter("all")}
              className={`px-3 py-1 text-xs rounded-full transition ${
                audienceFilter === "all"
                  ? "bg-slate-600/30 text-slate-200 border border-slate-500"
                  : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
              }`}
            >
              Todos
            </button>
            {["admin", "instructor", "student"].map((aud) => {
              const M = AUDIENCE_META[aud];
              return (
                <button
                  key={aud}
                  onClick={() => setAudienceFilter(aud)}
                  className={`px-3 py-1 text-xs rounded-full transition border ${
                    audienceFilter === aud
                      ? M.color
                      : "bg-slate-800/40 text-slate-400 border-slate-700 hover:bg-slate-800"
                  }`}
                >
                  {M.label}
                </button>
              );
            })}
          </div>

          <div className="space-y-3">
            {filtered.map((feat) => (
              <div
                key={feat.key}
                className="border border-slate-700 rounded-lg p-4 bg-slate-900/40"
                data-testid={`feature-item-${feat.key}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-700/50 text-slate-300 border border-slate-600">
                        {feat.category}
                      </span>
                      <h4 className="font-semibold text-slate-100">{feat.title}</h4>
                      {feat.status === "active" ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          Ativa
                        </span>
                      ) : feat.status === "beta" ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          Beta
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-red-500/20 text-red-300 border border-red-500/30">
                          Descontinuada
                        </span>
                      )}
                      {/* Audience badges */}
                      <div className="flex items-center gap-1">
                        {(feat.audience || []).map((aud) => {
                          const M = AUDIENCE_META[aud];
                          if (!M) return null;
                          const Icon = M.icon;
                          return (
                            <span
                              key={aud}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${M.color} inline-flex items-center gap-1`}
                              title={`Disponível para: ${M.label}`}
                            >
                              <Icon className="w-3 h-3" />
                              {M.label}
                            </span>
                          );
                        })}
                      </div>
                      <DateBadge date={feat.created_at} />
                    </div>

                    <p className="text-xs text-slate-400 mt-2 leading-relaxed">{feat.description}</p>

                    <ul className="mt-2 space-y-1">
                      {feat.capabilities.map((bullet, i) => (
                        <li
                          key={`${feat.key}-cap-${i}`}
                          className="text-[12px] text-slate-300 leading-relaxed pl-3 border-l-2 border-emerald-500/40"
                        >
                          {renderInlineMarkup(bullet, "emerald")}
                        </li>
                      ))}
                    </ul>

                    <p className="text-[10px] text-slate-600 mt-2 font-mono">
                      <Code2 className="w-3 h-3 inline mr-1" />
                      {feat.code_location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-8 text-slate-500">Sem funcionalidades que correspondam aos filtros.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DocsView — read-only catalogue of ALL business rules
// ─────────────────────────────────────────────────────────────
function DocsView({ docs, categoryFilter, setCategoryFilter, onEditRule }) {
  const categories = Object.keys(docs.by_category);
  const filtered =
    categoryFilter === "all"
      ? docs.docs
      : docs.docs.filter((d) => d.category === categoryFilter);

  return (
    <div className="space-y-4" data-testid="docs-view">
      <Card className="instrument-panel">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-cyan-400" />
              <div>
                <CardTitle className="font-chivo text-cockpit-text-bright">
                  Todas as Regras Aplicadas
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Documentação completa da lógica atual do sistema. {docs.editable_count} já são editáveis —
                  as restantes podem ser promovidas a editáveis no futuro.
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Category filter chips */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setCategoryFilter("all")}
              className={`px-3 py-1 text-xs rounded-full transition ${
                categoryFilter === "all"
                  ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/40"
                  : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
              }`}
            >
              Todas ({docs.total})
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1 text-xs rounded-full transition ${
                  categoryFilter === cat
                    ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/40"
                    : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
                }`}
              >
                {cat} ({docs.by_category[cat].length})
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {filtered.map((doc) => (
              <div
                key={doc.key}
                className="border border-slate-700 rounded-lg p-4 bg-slate-900/40"
                data-testid={`doc-item-${doc.key}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-700/50 text-slate-300 border border-slate-600">
                        {doc.category}
                      </span>
                      <h4 className="font-semibold text-slate-100">{doc.title}</h4>
                      {doc.editable_key ? (
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                          title="Pode ser editada na aba Editáveis"
                        >
                          <Edit2 className="w-3 h-3 inline mr-1" />
                          Editável
                        </span>
                      ) : (
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-600/30 text-slate-400 border border-slate-600"
                          title="Lógica no código — não editável"
                        >
                          <Lock className="w-3 h-3 inline mr-1" />
                          No código
                        </span>
                      )}
                      <DateBadge date={doc.created_at} />
                    </div>
                    <p className="text-xs text-slate-400 mt-2 leading-relaxed">{doc.description}</p>

                    <ul className="mt-2 space-y-1">
                      {doc.current_behavior.map((bullet, i) => (
                        <li
                          key={`${doc.key}-beh-${i}`}
                          className="text-[12px] text-slate-300 leading-relaxed pl-3 border-l-2 border-cyan-500/40"
                        >
                          {renderInlineMarkup(bullet, "cyan")}
                        </li>
                      ))}
                    </ul>

                    <p className="text-[10px] text-slate-600 mt-2 font-mono">
                      <Code2 className="w-3 h-3 inline mr-1" />
                      {doc.code_location}
                    </p>
                  </div>
                  {doc.editable_key && (
                    <Button
                      onClick={() => onEditRule(doc.editable_key)}
                      size="sm"
                      className="btn-cockpit text-xs flex-shrink-0"
                      data-testid={`jump-to-editable-${doc.key}`}
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Ir para editor
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Admin panel to view / edit / reset system rules (config engine).
 * Form-based editor driven by server-side `fields` metadata per rule.
 */
export default function RulesPanel() {
  const [rules, setRules] = useState([]);
  const [docs, setDocs] = useState(null); // {docs, by_category, total, editable_count}
  const [features, setFeatures] = useState(null); // {docs, by_category, total, active_count}
  const [view, setView] = useState("editable"); // "editable" | "docs" | "features" | "roadmap"
  const [docsCategoryFilter, setDocsCategoryFilter] = useState("all");
  const [featuresCategoryFilter, setFeaturesCategoryFilter] = useState("all");
  const [featuresAudienceFilter, setFeaturesAudienceFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // rule object
  const [formValues, setFormValues] = useState({}); // field_key -> value
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [rawMode, setRawMode] = useState(false); // fallback JSON mode
  const [rawText, setRawText] = useState("");

  const fetchRules = useCallback(async () => {
    setLoading(true);
    // Each endpoint is fetched independently — a failure in one doesn't
    // hide the others. The user can still browse Regras/Documentação even
    // if Funcionalidades happens to be temporarily broken, and vice-versa.
    const [rulesRes, docsRes, featuresRes] = await Promise.allSettled([
      axios.get(`${API}/admin/rules`, { withCredentials: true }),
      axios.get(`${API}/admin/rules-docs`, { withCredentials: true }),
      axios.get(`${API}/admin/features-docs`, { withCredentials: true }),
    ]);

    const failures = [];
    if (rulesRes.status === "fulfilled") {
      setRules(rulesRes.value.data.rules || []);
    } else {
      failures.push(`Regras: ${rulesRes.reason?.response?.status || ""} ${rulesRes.reason?.response?.data?.detail || rulesRes.reason?.message || "?"}`);
      console.error("[RulesPanel] /admin/rules failed:", rulesRes.reason);
    }
    if (docsRes.status === "fulfilled") {
      setDocs(docsRes.value.data);
    } else {
      failures.push(`Documentação: ${docsRes.reason?.response?.status || ""} ${docsRes.reason?.response?.data?.detail || docsRes.reason?.message || "?"}`);
      console.error("[RulesPanel] /admin/rules-docs failed:", docsRes.reason);
    }
    if (featuresRes.status === "fulfilled") {
      setFeatures(featuresRes.value.data);
    } else {
      failures.push(`Funcionalidades: ${featuresRes.reason?.response?.status || ""} ${featuresRes.reason?.response?.data?.detail || featuresRes.reason?.message || "?"}`);
      console.error("[RulesPanel] /admin/features-docs failed:", featuresRes.reason);
    }

    if (failures.length > 0) {
      toast.error(`Erro ao carregar: ${failures.join(" · ")}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const openEdit = (rule) => {
    setEditing(rule);
    setFormValues({ ...rule.value });
    setRawText(JSON.stringify(rule.value, null, 2));
    setFormError("");
    setRawMode(false);
  };

  const closeEdit = () => {
    setEditing(null);
    setFormValues({});
    setRawText("");
    setFormError("");
    setRawMode(false);
  };

  // Client-side validation mirroring server-side validators.
  const validateForm = (values, rule) => {
    if (!rule) return "";
    const fields = rule.fields || {};
    // Field-level checks
    for (const [key, meta] of Object.entries(fields)) {
      const v = values[key];
      if (v === "" || v === undefined || v === null) {
        return `Preenche o campo "${meta.label}".`;
      }
      if (meta.type === "integer" && !Number.isInteger(Number(v))) {
        return `"${meta.label}" tem de ser um número inteiro.`;
      }
      if ((meta.type === "number" || meta.type === "percent") && Number.isNaN(Number(v))) {
        return `"${meta.label}" tem de ser um número.`;
      }
      if (meta.min !== undefined && Number(v) < meta.min) {
        return `"${meta.label}" tem de ser >= ${meta.min}.`;
      }
      if (meta.max !== undefined && Number(v) > meta.max) {
        return `"${meta.label}" tem de ser <= ${meta.max}.`;
      }
    }
    // Cross-field validators (same shape as server-side specs)
    for (const spec of rule.validators || []) {
      const [vrule, params] = spec.split(":");
      const keys = (params || "").split(",").map((s) => s.trim());
      if (vrule === "sum_must_equal_100") {
        const sum = keys.reduce((acc, k) => acc + Number(values[k] || 0), 0);
        if (Math.abs(sum - 100) > 0.01) {
          const labels = keys.map((k) => fields[k]?.label || k).join(" + ");
          return `${labels} tem de somar 100% (atual: ${sum}%).`;
        }
      }
      if (vrule === "sum_must_equal_1") {
        const sum = keys.reduce((acc, k) => acc + Number(values[k] || 0), 0);
        if (Math.abs(sum - 1) > 0.001) {
          const labels = keys.map((k) => fields[k]?.label || k).join(" + ");
          return `${labels} tem de somar 1.0 (atual: ${sum.toFixed(3)}).`;
        }
      }
    }
    return "";
  };

  const handleFieldChange = (fieldKey, rawValue, meta) => {
    let coerced = rawValue;
    if (meta.type === "integer") {
      coerced = rawValue === "" ? "" : parseInt(rawValue, 10);
    } else if (meta.type === "number" || meta.type === "percent") {
      coerced = rawValue === "" ? "" : parseFloat(rawValue);
    } else if (meta.type === "boolean") {
      coerced = !!rawValue;
    }
    const next = { ...formValues, [fieldKey]: coerced };
    setFormValues(next);
    setFormError(validateForm(next, editing));
  };

  const handleSave = async () => {
    let valueToSend;
    if (rawMode) {
      try {
        valueToSend = JSON.parse(rawText);
      } catch (e) {
        toast.error(`JSON inválido: ${e.message}`);
        return;
      }
    } else {
      const err = validateForm(formValues, editing);
      if (err) {
        setFormError(err);
        toast.error(err);
        return;
      }
      valueToSend = formValues;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/rules/${editing.key}`,
        { value: valueToSend },
        { withCredentials: true },
      );
      toast.success("Regra guardada com sucesso");
      closeEdit();
      await fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao guardar regra");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (rule) => {
    if (!window.confirm(`Repor a regra "${rule.key}" para o valor por defeito?`)) return;
    try {
      await axios.post(`${API}/admin/rules/${rule.key}/reset`, {}, { withCredentials: true });
      toast.success("Regra revertida para o valor por defeito");
      await fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao repor regra");
    }
  };

  const prettyValue = (rule) => {
    if (!rule.fields || Object.keys(rule.fields).length === 0) {
      return JSON.stringify(rule.value);
    }
    return Object.entries(rule.fields)
      .map(([k, meta]) => `${meta.label}: ${rule.value[k]}${meta.unit ? ` ${meta.unit}` : ""}`)
      .join(" · ");
  };

  return (
    <div className="space-y-4" data-testid="rules-panel">
      {/* View switcher */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setView("editable")}
          className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
            view === "editable"
              ? "bg-orange-500/20 text-orange-300 border border-orange-500/40"
              : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
          }`}
          data-testid="view-editable-btn"
        >
          Editáveis ({rules.length})
        </button>
        <button
          type="button"
          onClick={() => setView("docs")}
          className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
            view === "docs"
              ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
              : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
          }`}
          data-testid="view-docs-btn"
        >
          Todas as regras aplicadas ({docs?.total || 0})
        </button>
        <button
          type="button"
          onClick={() => setView("features")}
          className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
            view === "features"
              ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
              : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
          }`}
          data-testid="view-features-btn"
        >
          Funcionalidades ({features?.total || 0})
        </button>
        <button
          type="button"
          onClick={() => setView("roadmap")}
          className={`px-3 py-1.5 rounded text-xs font-semibold transition flex items-center gap-1.5 ${
            view === "roadmap"
              ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
              : "bg-slate-800/40 text-slate-400 border border-slate-700 hover:bg-slate-800"
          }`}
          data-testid="view-roadmap-btn"
        >
          <ListTodo className="w-3.5 h-3.5" />
          Roadmap / Ideias
        </button>
      </div>

      {view === "roadmap" ? (
        <RoadmapPanel />
      ) : view === "features" && features ? (
        <FeaturesView
          features={features}
          categoryFilter={featuresCategoryFilter}
          setCategoryFilter={setFeaturesCategoryFilter}
          audienceFilter={featuresAudienceFilter}
          setAudienceFilter={setFeaturesAudienceFilter}
        />
      ) : view === "docs" && docs ? (
        <DocsView
          docs={docs}
          categoryFilter={docsCategoryFilter}
          setCategoryFilter={setDocsCategoryFilter}
          onEditRule={(editableKey) => {
            const rule = rules.find((r) => r.key === editableKey);
            if (rule) {
              setView("editable");
              openEdit(rule);
            } else {
              toast.error("Regra editável não encontrada.");
            }
          }}
        />
      ) : (
      <Card className="instrument-panel">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Settings2 className="w-6 h-6 text-cyan-400" />
              <div>
                <CardTitle className="font-chivo text-cockpit-text-bright">Regras do Sistema</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Parâmetros de negócio editáveis sem alterar código. Mudanças entram em vigor imediatamente (cache 5 min).
                </p>
              </div>
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Info className="w-4 h-4" />
              {rules.filter((r) => r.is_customized).length} de {rules.length} regras customizadas
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">A carregar regras…</div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Nenhuma regra registada.</div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div
                  key={rule.key}
                  className="border border-slate-700 rounded-lg p-4 bg-slate-900/40"
                  data-testid={`rule-item-${rule.key}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-slate-100 capitalize">
                          {rule.key.replace(/_/g, " ")}
                        </h4>
                        {rule.is_customized ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            Customizado
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-600/30 text-slate-400 border border-slate-600">
                            Por defeito
                          </span>
                        )}
                        {rule.is_critical && (
                          <span
                            className="px-2 py-0.5 rounded text-[10px] font-semibold bg-red-500/20 text-red-300 border border-red-500/30"
                            title="Regra crítica — não pode ser eliminada"
                          >
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            Crítica
                          </span>
                        )}
                        {rule.is_sensitive && (
                          <span
                            className="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-500/20 text-purple-200 border border-purple-500/40"
                            title="Lógica pedagógica sensível — altera apenas com muito cuidado"
                          >
                            ⚠ Pedagógica
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{rule.description}</p>

                      {/* Current values as a pretty list (not JSON) */}
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {rule.fields && Object.keys(rule.fields).length > 0 ? (
                          Object.entries(rule.fields).map(([k, meta]) => (
                            <div key={k} className="flex items-baseline justify-between gap-2 p-2 bg-slate-950/50 rounded border border-slate-800">
                              <span className="text-xs text-slate-400">{meta.label}</span>
                              <span className="text-sm font-mono text-emerald-300">
                                {rule.value[k]}
                                {meta.unit ? <span className="text-slate-500 ml-1">{meta.unit}</span> : null}
                              </span>
                            </div>
                          ))
                        ) : (
                          <code className="col-span-2 text-[11px] text-emerald-300 font-mono">{JSON.stringify(rule.value)}</code>
                        )}
                      </div>

                      {rule.updated_by && (
                        <p className="text-[10px] text-slate-500 mt-2 font-mono">
                          Atualizado por <span className="text-slate-400">{rule.updated_by}</span>
                          {rule.updated_at && <> em {new Date(rule.updated_at).toLocaleString("pt-PT")}</>}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        onClick={() => openEdit(rule)}
                        className="btn-cockpit text-xs"
                        size="sm"
                        data-testid={`edit-rule-${rule.key}`}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      {rule.is_customized && (
                        <Button
                          onClick={() => handleReset(rule)}
                          variant="outline"
                          size="sm"
                          className="btn-secondary-cockpit text-xs"
                          data-testid={`reset-rule-${rule.key}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Repor
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-orange-400" />
              Editar: {editing?.key?.replace(/_/g, " ")}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-slate-900/60 border border-slate-700 rounded text-xs text-slate-400">
                {editing.description}
              </div>

              {editing.is_sensitive && (
                <div
                  className="p-3 bg-purple-500/10 border border-purple-500/40 rounded text-xs text-purple-100 flex items-start gap-2"
                  data-testid="sensitive-warning"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-purple-300" />
                  <div>
                    <p className="font-semibold text-purple-200">Lógica pedagógica sensível</p>
                    <p className="mt-1 leading-relaxed">
                      Esta regra faz parte do núcleo do modelo de progresso dos alunos. Alterar estes
                      valores muda o SIGNIFICADO das métricas exibidas (ex: o que significa &quot;estável&quot; ou
                      &quot;dominado&quot;). Modifica apenas se tiveres razão pedagógica concreta — não sirvas de
                      base para experiências tipo A/B.
                    </p>
                  </div>
                </div>
              )}

              {!rawMode && editing.fields && Object.keys(editing.fields).length > 0 ? (
                <>
                  <div className="space-y-3">
                    {Object.entries(editing.fields).map(([fieldKey, meta]) => (
                      <div key={fieldKey} className="space-y-1">
                        <label className="text-xs uppercase tracking-wider text-slate-400 flex items-center justify-between gap-2">
                          <span>{meta.label}</span>
                          <span className="text-[10px] text-slate-500 normal-case">
                            defeito: {editing.default_value[fieldKey]}{meta.unit ? ` ${meta.unit}` : ""}
                          </span>
                        </label>
                        <div className="flex items-center gap-2">
                          {meta.type === "boolean" ? (
                            <input
                              type="checkbox"
                              checked={!!formValues[fieldKey]}
                              onChange={(e) => handleFieldChange(fieldKey, e.target.checked, meta)}
                              className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-orange-500"
                              data-testid={`rule-field-${fieldKey}`}
                            />
                          ) : (
                            <Input
                              type="number"
                              value={formValues[fieldKey] ?? ""}
                              onChange={(e) => handleFieldChange(fieldKey, e.target.value, meta)}
                              min={meta.min}
                              max={meta.max}
                              step={meta.step || (meta.type === "integer" ? 1 : 0.01)}
                              className="input-cockpit flex-1"
                              data-testid={`rule-field-${fieldKey}`}
                            />
                          )}
                          {meta.unit && (
                            <span className="text-sm text-slate-400 min-w-[3rem]">{meta.unit}</span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 leading-snug">{meta.help}</p>
                      </div>
                    ))}
                  </div>

                  {formError && (
                    <p className="text-xs text-red-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {formError}
                    </p>
                  )}
                  {!formError && Object.keys(formValues).length > 0 && (
                    <p className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Valores válidos
                    </p>
                  )}
                </>
              ) : (
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500">Valor (JSON bruto)</label>
                  <Textarea
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                    className="input-cockpit mt-1 font-mono text-xs h-48"
                    data-testid="rule-raw-textarea"
                  />
                </div>
              )}

              {/* Toggle advanced/raw mode only when schema exists */}
              {editing.fields && Object.keys(editing.fields).length > 0 && (
                <button
                  type="button"
                  onClick={() => setRawMode((m) => !m)}
                  className="text-[11px] text-slate-500 hover:text-slate-300 flex items-center gap-1"
                  data-testid="toggle-raw-mode"
                >
                  <Code2 className="w-3 h-3" />
                  {rawMode ? "Voltar ao formulário" : "Modo avançado (JSON bruto)"}
                </button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={closeEdit} variant="outline" className="btn-secondary-cockpit">
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || (!rawMode && !!formError)}
              className="btn-cockpit"
              data-testid="save-rule-btn"
            >
              {saving ? "A guardar…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
